import { axiosInstance } from './api';

// VISITS-1 (#175): group mechanism admin API. Adding a zone to a branch
// group, or a user to a user group, takes effect on the very next
// has_permission() call on the backend — no cache, no relogin.

export interface UserGroupSummary {
  id: number;
  name: string;
  member_count: number;
}

export interface UserGroupMember {
  membership_id: number;
  user_id: number;
  username: string;
}

export interface UserGroupGrant {
  grant_id: number;
  branch_group_id: number;
  branch_group_name: string;
  role: string;
}

export interface UserGroupDetail {
  id: number;
  name: string;
  members: UserGroupMember[];
  grants: UserGroupGrant[];
}

export interface BranchGroupSummary {
  id: number;
  name: string;
  zone_count: number;
}

export interface BranchGroupZone {
  membership_id: number;
  zone_id: number;
  zone_name: string;
}

export interface BranchGroupGrant {
  grant_id: number;
  user_group_id: number;
  user_group_name: string;
  role: string;
}

export interface BranchGroupDetail {
  id: number;
  name: string;
  zones: BranchGroupZone[];
  grants: BranchGroupGrant[];
  /** Администраторы группы (#1969) — служебная группа «<группа> · админы» с ролью zone_admin. */
  admins: { user_id: number; username: string }[];
}

const USER_GROUPS_BASE = '/core/api/admin/user-groups/';
const BRANCH_GROUPS_BASE = '/core/api/admin/branch-groups/';

export const listUserGroups = () =>
  axiosInstance.get<{ results: UserGroupSummary[] }>(USER_GROUPS_BASE);

export const createUserGroup = (name: string) =>
  axiosInstance.post<UserGroupSummary>(USER_GROUPS_BASE, { name });

export const getUserGroup = (groupId: number | string) =>
  axiosInstance.get<UserGroupDetail>(`${USER_GROUPS_BASE}${groupId}/`);

export const renameUserGroup = (groupId: number | string, name: string) =>
  axiosInstance.patch<UserGroupDetail>(`${USER_GROUPS_BASE}${groupId}/`, { name });

export const deleteUserGroup = (groupId: number | string) =>
  axiosInstance.delete(`${USER_GROUPS_BASE}${groupId}/`);

export const addUserGroupMember = (groupId: number | string, userId: number) =>
  axiosInstance.post<UserGroupDetail>(`${USER_GROUPS_BASE}${groupId}/members/`, { user_id: userId });

export const removeUserGroupMember = (groupId: number | string, userId: number) =>
  axiosInstance.delete(`${USER_GROUPS_BASE}${groupId}/members/${userId}/`);

export const listBranchGroups = () =>
  axiosInstance.get<{ results: BranchGroupSummary[] }>(BRANCH_GROUPS_BASE);

export const createBranchGroup = (name: string) =>
  axiosInstance.post<BranchGroupSummary>(BRANCH_GROUPS_BASE, { name });

export const getBranchGroup = (groupId: number | string) =>
  axiosInstance.get<BranchGroupDetail>(`${BRANCH_GROUPS_BASE}${groupId}/`);

export const renameBranchGroup = (groupId: number | string, name: string) =>
  axiosInstance.patch<BranchGroupDetail>(`${BRANCH_GROUPS_BASE}${groupId}/`, { name });

export const deleteBranchGroup = (groupId: number | string) =>
  axiosInstance.delete(`${BRANCH_GROUPS_BASE}${groupId}/`);

export const addBranchGroupZone = (groupId: number | string, zoneId: number) =>
  axiosInstance.post<BranchGroupDetail>(`${BRANCH_GROUPS_BASE}${groupId}/zones/`, { zone_id: zoneId });

export const removeBranchGroupZone = (groupId: number | string, zoneId: number) =>
  axiosInstance.delete(`${BRANCH_GROUPS_BASE}${groupId}/zones/${zoneId}/`);

/** Create or update (role change) the grant of `userGroupId` on `branchGroupId`. */
export const setBranchGroupGrant = (groupId: number | string, userGroupId: number, role: string) =>
  axiosInstance.post<BranchGroupDetail>(`${BRANCH_GROUPS_BASE}${groupId}/grants/`, {
    user_group_id: userGroupId,
    role,
  });

export const removeBranchGroupGrant = (groupId: number | string, grantId: number) =>
  axiosInstance.delete(`${BRANCH_GROUPS_BASE}${groupId}/grants/${grantId}/`);

export const addBranchGroupAdmin = (groupId: number | string, userId: number) =>
  axiosInstance.post<BranchGroupDetail>(`/core/api/admin/branch-groups/${groupId}/admins/`, { user_id: userId });

export const removeBranchGroupAdmin = (groupId: number | string, userId: number) =>
  axiosInstance.delete<BranchGroupDetail>(`/core/api/admin/branch-groups/${groupId}/admins/${userId}/`);
